import { defineStore } from 'pinia';
import { ref } from 'vue';
import { GetReportsList } from '@/services/Reports/GetReportsList';
import { ReportTypesEnum } from '@/enums/reportTypes';

import { GetCompanyWithEmployeeViewing } from '../services/Companies/GetCompaniesWithEmployeeViewing';
import { GetCompanyPolicies } from '../services/Companies/GetCompanyPolicies';
import { GetFeatureFlagList } from '@/services/FeatureFlags/GetFeatureFlagList';
import { FeatureFlagIdEnum } from '@/enums/featureFlagIds';

export const useBreakMonitoringReportStore = defineStore('breakMonitoringReport', () => {
  const isInitialState = ref(true);
  const isLoading = ref(false);
  const isFeatureFlagOn = ref(false);
  const isBreakLogsActive = ref(true);
  const exportedLogs = ref([]);
  const exportedBreakMonitoringLogs = ref([]);
  const oldBreakMonitoringFeatureFlag = ref(false);
  const newBreakMonitoringFeatureFlag = ref(false);
  const exportedLogsPagination = ref({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const exportedBreakMonitoringLogsPagination = ref({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const isExportedLogsLoading = ref(false);
  const isExportedLogsInitialState = ref(true);
  const getExportedBreakLogsReport = async ({ PageNumber, RowsPerPage }) => {
    isExportedLogsLoading.value = true;
    try {
      const response = await GetReportsList({
        PageNumber,
        RowsPerPage,
        ReportType: ReportTypesEnum.breakLogs,
      });
      if (response.errors) {
        exportedLogs.value = [];
        return;
      }
      exportedLogs.value = response.data;
      exportedLogsPagination.value = response.pagination;
    } finally {
      isExportedLogsInitialState.value = false;
      isExportedLogsLoading.value = false;
    }
  };

  const getExportedBreakMonitoringLogsReport = async ({ PageNumber, RowsPerPage }) => {
    isExportedLogsLoading.value = true;
    try {
      const response = await GetReportsList({
        PageNumber,
        RowsPerPage,
        ReportType: ReportTypesEnum.breakMonitoringReport,
      });
      if (response.errors) {
        exportedBreakMonitoringLogs.value = [];
        return;
      }

      exportedBreakMonitoringLogs.value = response.data;
      exportedBreakMonitoringLogsPagination.value = response.pagination;
    } finally {
      isExportedLogsInitialState.value = false;
      isExportedLogsLoading.value = false;
    }
  };

  const getFeatureFlagList = async () => {
    let featureFlagList = await GetFeatureFlagList();

    featureFlagList.forEach((featureFlag) => {
      if (featureFlag.keyId === FeatureFlagIdEnum.breakMonitoringWidget) {
        oldBreakMonitoringFeatureFlag.value = featureFlag.keyValue;
      }
      if (featureFlag.keyId === FeatureFlagIdEnum.breakMonitoringAttendanceReport) {
        newBreakMonitoringFeatureFlag.value = featureFlag.keyValue;
      }
    });
  };

  const companySelect = ref({
    selectedCompanyID: '',
    selectedCompanyName: '',
    searchString: '',
    listState: false,
    list: [],
    listLoader: false,
    listCurrentPage: 1,
    listTotalPages: 0,
    isCompanyFirstLoadEmpty: false,
  });

  const getCompanyList = async (pageNumber) => {
    companySelect.value.listLoader = true;

    let rowsPerPage = 10;
    let sortColumn = 'CompanyName';
    let sortOrder = 'asc';

    try {
      let getCompanyResponse = await GetCompanyWithEmployeeViewing(
        companySelect.value.searchString,
        pageNumber,
        rowsPerPage,
        sortColumn,
        sortOrder,
      );

      companySelect.value.list = companySelect.value.list.concat(getCompanyResponse.paginatedList.data);

      let { currentPage, totalPages } = getCompanyResponse.paginatedList.pagination;

      setCompanyPaginations(currentPage, totalPages);
    } catch (error) {
      console.error('Failed to fetch company list:', error);
    } finally {
      companySelect.value.listLoader = false;
    }
  };

  const setCompanyPaginations = (currentPage, totalPages) => {
    companySelect.value.listCurrentPage = currentPage;
    companySelect.value.listTotalPages = totalPages;
  };

  const isCompanyPolicyBreakMonitoringEnabled = ref(false);
  const companyPolicyErrorMessage = ref('');

  const getCompanyPolicies = async (companyId) => {
    isInitialState.value = false;
    try {
      let response = await GetCompanyPolicies(companyId);

      const { data } = response;
      const breakMonitoringPolicy = data.find((policy) => policy.propertyName === 'enableBreakMonitoring');

      if (breakMonitoringPolicy) {
        if (breakMonitoringPolicy.value === true || breakMonitoringPolicy.value === 'true') {
          companyPolicyErrorMessage.value = '';
          isCompanyPolicyBreakMonitoringEnabled.value = true;
          companySelect.value.selectedCompanyID = companyId;
        } else {
          companyPolicyErrorMessage.value =
            'This company is not monitoring breaks. Please reach out to your HR Admin for assistance.';
          isCompanyPolicyBreakMonitoringEnabled.value = false;
          companySelect.value.selectedCompanyID = '';
        }
      } else {
        companyPolicyErrorMessage.value =
          'This company is not monitoring breaks. Please reach out to your HR Admin for assistance.';
        isCompanyPolicyBreakMonitoringEnabled.value = false;
        companySelect.value.selectedCompanyID = '';
      }
    } catch (error) {
      console.log(error);
    }
  };

  return {
    isInitialState,
    isLoading,
    isFeatureFlagOn,
    isBreakLogsActive,
    exportedLogs,
    exportedBreakMonitoringLogs,
    exportedLogsPagination,
    exportedBreakMonitoringLogsPagination,
    isExportedLogsLoading,
    isExportedLogsInitialState,
    getExportedBreakLogsReport,
    getExportedBreakMonitoringLogsReport,
    getFeatureFlagList,
    newBreakMonitoringFeatureFlag,
    oldBreakMonitoringFeatureFlag,
    getCompanyList,
    companySelect,
    getCompanyPolicies,
    isCompanyPolicyBreakMonitoringEnabled,
    companyPolicyErrorMessage,
  };
});
